import * as React from 'react'
import { Helmet } from 'react-helmet'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import ScrollToTopOnMount from '../../components/Effects/ScrollToTopOnMount'
import moment from '../../utilities/Moment'
import { AccountingSyncController, IntegrationsController } from '../../controllers/accountant'
import { AccountantApiError } from '../../controllers/accountant/request'
import { AccountantAccountingSync, AccountantIntegration, AccountantWorkspace } from '../../types'
import { useAccountingSync } from '../../hooks/use-accounting-sync'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { Card, CardAction, CardDescription, CardFooter, CardHeader, CardTitle } from '../../components/ui/card'
import { Skeleton } from '../../components/ui/skeleton'
import { Spinner } from '../../components/ui/spinner'
import { CheckIcon, RefreshCwIcon, SendIcon } from 'lucide-react'

interface IProps {
  workspace: AccountantWorkspace
  onNotFound: () => void
}

export default function Integrations(props: IProps) {
  const { workspace } = props
  const { t } = useTranslation()

  const [integrations, setIntegrations] = React.useState<AccountantIntegration[]>([])
  const [didInitialLoad, setDidInitialLoad] = React.useState(false)
  const [requestingType, setRequestingType] = React.useState<string | null>(null)

  // Ignore responses that arrive after the workspace was switched
  const activeWorkspaceId = React.useRef(workspace.id)
  activeWorkspaceId.current = workspace.id

  const fetchIntegrations = () => {
    const requestedWorkspaceId = workspace.id

    IntegrationsController
      .getIntegrations(workspace.id)
      .then(response => {
        if (activeWorkspaceId.current !== requestedWorkspaceId) return

        setIntegrations(response)
        setDidInitialLoad(true)
      })
      .catch(error => {
        if (activeWorkspaceId.current !== requestedWorkspaceId) return
        if (error instanceof AccountantApiError && error.status === 404) props.onNotFound()
        else console.error(error)
      })
  }

  React.useEffect(() => {
    setDidInitialLoad(false)
    fetchIntegrations()
  }, [workspace.id])

  const { status: syncStatus, syncing, sync } = useAccountingSync<AccountantAccountingSync>({
    getStatus: () => AccountingSyncController.getStatus(workspace.id),
    synchronize: () => AccountingSyncController.synchronize(workspace.id).then(response => !response.error),
    onCompleted: () => toast.success(t('Accountant::Synchronization completed')),
    key: workspace.id,
  })

  const onSyncClick = async () => {
    if (await sync()) {
      toast.success(t('Accountant::Synchronization started'))
    } else {
      toast.error(t('Accountant::Something went wrong, please try again'))
    }
  }

  const onRequestActivation = async (integration: AccountantIntegration) => {
    const requestedWorkspaceId = workspace.id
    setRequestingType(integration.type)

    try {
      const updated = await IntegrationsController.requestActivation(workspace.id, integration.type)
      if (activeWorkspaceId.current !== requestedWorkspaceId) return

      setIntegrations(current => current.map(i => i.type === updated.type ? updated : i))
      toast.success(t('Accountant::Request sent to {{businessName}}', { businessName: workspace.business_name, interpolation: { escapeValue: false } }))
    } catch (error) {
      if (activeWorkspaceId.current !== requestedWorkspaceId) return

      if (error instanceof AccountantApiError && (error.status === 429 || error.status === 422)) {
        fetchIntegrations()
      } else if (error instanceof AccountantApiError && error.status === 404) {
        props.onNotFound()
      } else {
        console.error(error)
        toast.error(t('Accountant::Something went wrong, please try again'))
      }
    } finally {
      setRequestingType(null)
    }
  }

  return (
    <>
      <Helmet>
        <title>{t('Accountant::Bizzey | Integrations')}</title>
      </Helmet>
      <ScrollToTopOnMount />

      <p className="mb-4 text-sm text-muted-foreground">
        {t('Accountant::Ask {{businessName}} to connect their accounting software. They receive an e-mail with a direct link to set it up.', { businessName: workspace.business_name, interpolation: { escapeValue: false } })}
      </p>

      {syncStatus?.can_sync && (
        <Card className="mb-4">
          <CardHeader>
            <CardTitle>{t('Accountant::Synchronize to accounting software')}</CardTitle>
            <CardDescription>
              {syncStatus.last_accounting_sync_at
                ? t('Accountant::Last synchronized on {{date}}', { date: moment(syncStatus.last_accounting_sync_at).format(`${workspace.setting.date_format} HH:mm`), interpolation: { escapeValue: false } })
                : t('Accountant::Not synchronized yet')}
            </CardDescription>
            <CardAction>
              <Button variant="outline" disabled={syncing} onClick={onSyncClick}>
                {syncing ? <Spinner /> : <RefreshCwIcon />}
                {syncing ? t('Accountant::Synchronizing...') : t('Accountant::Synchronize now')}
              </Button>
            </CardAction>
          </CardHeader>
        </Card>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        {!didInitialLoad && Array.from({ length: 4 }).map((_, index) => (
          <Skeleton key={index} className="h-36 w-full" />
        ))}

        {didInitialLoad && integrations.map(integration => (
          <Card key={integration.type}>
            <CardHeader className="flex-1 content-start">
              <CardTitle className="flex items-center gap-2">
                <img src={integration.icon} alt="" className="size-6 shrink-0 rounded object-contain" />
                {integration.name}
              </CardTitle>
              <CardDescription>{integration.description}</CardDescription>
              <CardAction>
                {integration.connected
                  ? <Badge><CheckIcon />{t('Accountant::Connected')}</Badge>
                  : <Badge variant="outline">{t('Accountant::Not connected')}</Badge>}
              </CardAction>
            </CardHeader>

            {!integration.connected && (
              <CardFooter className="flex flex-col items-start gap-2">
                <Button
                  variant="outline"
                  disabled={!integration.can_request || requestingType === integration.type}
                  onClick={() => onRequestActivation(integration)}
                >
                  {requestingType === integration.type ? <Spinner /> : <SendIcon />}
                  {integration.requested_at ? t('Accountant::Resend request') : t('Accountant::Request activation')}
                </Button>

                {integration.requested_at && (
                  <p className="text-xs text-muted-foreground">
                    {t('Accountant::Requested on {{date}}', { date: moment(integration.requested_at).format(workspace.setting.date_format), interpolation: { escapeValue: false } })}
                    {!integration.can_request && ` · ${t('Accountant::You can resend this request after 24 hours')}`}
                  </p>
                )}
              </CardFooter>
            )}
          </Card>
        ))}
      </div>
    </>
  )
}
